import {
  IWidgetConfigurationProps,
  withWidgetConfiguration,
} from "@sgwt-widget/core";
import { h } from "preact";
import { ApiBackedComponent } from "../../common/components/ApiBackedComponent";
import { SimpleGrid } from "../../common/components/SimpleGrid";
import { MakeGenericApiCall } from "../../common/generic/generic-api";
import {
  AddFpvBlFilter,
  BuildGenericRequest,
  CheckReponseDataForError,
} from "../../common/generic/generic-helper";
import { NbiDimensions } from "../../common/generic/generic-nbi-dimensions";
import { IFilterItem } from "../../common/generic/generic-types";
import { ITakeClientAndDateProps } from "../../common/utils/types";
import { mapData } from "../utils/helper";
import { IYtdNbiTopOutput } from "../utils/types";

const kpis = "nbi_ytd";
const salesAttributes = "salesben_sales,fpv_ssbl";
const clientAttributes = "client_lename,client_leid";
const dealAttributes =
  "client_lename,salesben_sales,product_l3,gbis_credit_date";
const sortOrder = "-nbi_ytd";
const top = 5;
const collapsedClass = " collapsed";
const panelCollapsedClass = "panel-collapse collapse";

interface IState {
  data: IYtdNbiTopOutput;
  dealsColumnDefs: any[];
  leColumnDefs: any[];
  salesColumnDefs: any[];
  showDealPanel: boolean;
  showLePanel: boolean;
  showSalesPanel: boolean;
}

export class NbiYtdTopsClass extends ApiBackedComponent<
  ITakeClientAndDateProps & IWidgetConfigurationProps,
  IState
> {
  public constructor() {
    super();
    this.setState({
      showDealPanel: false,
      showLePanel: false,
      showSalesPanel: true,
    });
  }

  protected shouldFetchApiData(nextProps: ITakeClientAndDateProps): boolean {
    return this.props.client !== nextProps.client;
  }

  protected renderWithDataReady(): JSX.Element {
    return (
      <div class="panel-group">
        <div class="panel panel-default">
          <div class="panel-heading">
            <h4 class="panel-title">
              <a
                role="button"
                class={this.state.showSalesPanel ? collapsedClass : ""}
                onClick={() =>
                  this.panelVisibility(!this.state.showSalesPanel, false, false)
                }
              >
                Top Sales
              </a>
            </h4>
          </div>
          <div
            class={
              panelCollapsedClass + (this.state.showSalesPanel ? " in" : "")
            }
          >
            <div class="panel-body">
              <SimpleGrid
                rowData={this.state.data.TopSales}
                columnDefs={this.state.salesColumnDefs}
              />
            </div>
          </div>
        </div>
        <div class="panel panel-default">
          <div class="panel-heading">
            <h4 class="panel-title">
              <a
                role="button"
                class={this.state.showLePanel ? collapsedClass : ""}
                onClick={() =>
                  this.panelVisibility(false, !this.state.showLePanel, false)
                }
              >
                Top Legal Entities
              </a>
            </h4>
          </div>
          <div
            class={panelCollapsedClass + (this.state.showLePanel ? " in" : "")}
          >
            <div class="panel-body">
              <SimpleGrid
                rowData={this.state.data.TopLegalEntities}
                columnDefs={this.state.leColumnDefs}
              />
            </div>
          </div>
        </div>
        <div class="panel panel-default">
          <div class="panel-heading">
            <h4 class="panel-title">
              <a
                role="button"
                class={this.state.showDealPanel ? collapsedClass : ""}
                onClick={() =>
                  this.panelVisibility(false, false, !this.state.showDealPanel)
                }
              >
                Top Deals
              </a>
            </h4>
          </div>
          <div
            class={
              panelCollapsedClass + (this.state.showDealPanel ? " in" : "")
            }
          >
            <div class="panel-body">
              <SimpleGrid
                rowData={this.state.data.TopDeals}
                columnDefs={this.state.dealsColumnDefs}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // tslint:disable-next-line:cognitive-complexity
  protected async fetchApiData(props: ITakeClientAndDateProps) {
    this.initCorrelationId();
    const headers = props.headers;
    if (
      props.client === undefined ||
      isNaN(parseInt(props.client.bdrCommercialId, 10))
    ) {
      this.setState({
        data: { TopDeals: [], TopLegalEntities: [], TopSales: [] },
      });
      this.setDataStatus("no-client");
      return;
    }
    this.setDataStatus("loading");
    const attr = new NbiDimensions();
    const filters: IFilterItem[] = [
      {
        attribute: attr.CLIENT_GEN_ID,
        equals: true,
        value: props.client.bdrCommercialId,
      },
    ];
    const salesFilters: IFilterItem[] = [
      {
        attribute: attr.SALESBEN_SALES,
        equals: false,
        value: "NOSALES",
      },
      {
        attribute: attr.CLIENT_GEN_ID,
        equals: true,
        value: props.client.bdrCommercialId,
      },
    ];
    AddFpvBlFilter(filters, attr.FPV_BL, props.client.fpv_bl);
    AddFpvBlFilter(salesFilters, attr.FPV_BL, props.client.fpv_bl);
    const date =
      props.client.value === undefined
        ? undefined
        : new Date(props.client.value);
    const salesGenericRequest = BuildGenericRequest(
      kpis,
      date,
      salesAttributes,
      top,
      undefined,
      undefined,
      salesFilters,
      sortOrder,
      sortOrder
    );
    const salesResponseData = await MakeGenericApiCall(
      headers,
      "nbi",
      salesGenericRequest,
      this.props.widgetConfiguration,
      this.state.correlationId
    );

    const clientGenericRequest = BuildGenericRequest(
      kpis,
      date,
      clientAttributes,
      top,
      undefined,
      undefined,
      filters,
      sortOrder,
      sortOrder
    );
    const clientResponseData = await MakeGenericApiCall(
      headers,
      "nbi",
      clientGenericRequest,
      this.props.widgetConfiguration,
      this.state.correlationId
    );

    const dealGenericRequest = BuildGenericRequest(
      kpis,
      date,
      dealAttributes,
      top,
      undefined,
      undefined,
      filters,
      sortOrder,
      sortOrder
    );
    const dealResponseData = await MakeGenericApiCall(
      headers,
      "nbi",
      dealGenericRequest,
      this.props.widgetConfiguration,
      this.state.correlationId
    );
    const salesErrorStatus = CheckReponseDataForError(salesResponseData);
    const clientErrorStatus = CheckReponseDataForError(clientResponseData);
    const dealErrorStatus = CheckReponseDataForError(dealResponseData);

    const errorStatus =
      salesErrorStatus && clientErrorStatus && dealErrorStatus;
    if (errorStatus) {
      this.setState({
        data: { TopDeals: [], TopLegalEntities: [], TopSales: [] },
      });
      this.setDataStatus(errorStatus);
      this.setStatusText(dealResponseData.statusText);
    } else if (
      salesResponseData.noClientMapping &&
      clientResponseData.noClientMapping &&
      dealResponseData.noClientMapping
    ) {
      this.setState({
        data: { TopDeals: [], TopLegalEntities: [], TopSales: [] },
      });
      this.setDataStatus("no-client-mapping");
    } else if (
      salesResponseData.serverError &&
      clientResponseData.serverError &&
      dealResponseData.serverError
    ) {
      this.setState({
        data: { TopDeals: [], TopLegalEntities: [], TopSales: [] },
      });
      this.setDataStatus("server-error");
    } else {
      const result = mapData(
        salesResponseData,
        dealResponseData,
        clientResponseData
      );

      if (result) {
        if (
          result.TopDeals.length === 0 &&
          result.TopSales.length === 0 &&
          result.TopLegalEntities.length === 0
        ) {
          this.setState({
            data: result,
          });
          this.setDataStatus("no-data");
        } else {
          this.setState({
            data: result,
            dealsColumnDefs: [
              { headerName: "#", field: "Id" },
              { headerName: "Date", field: "Date", headerstyle: "width: 75px" },
              { headerName: "Sales Beneficiary", field: "SalesName" },
              { headerName: "Client", field: "LEFullName" },
              { headerName: "Product L3", field: "ProductLevel3" },
              { headerName: "NBI (K€)", field: "Ytd" },
            ],
            leColumnDefs: [
              { headerName: "#", field: "Id" },
              { headerName: "Client", field: "LEFullName", isHyperLink: true },
              { headerName: "BDR CCial ID", field: "ClientId" },
              { headerName: "NBI (K€)", field: "Ytd" },
            ],
            salesColumnDefs: [
              { headerName: "#", field: "Id" },
              { headerName: "Sales", field: "SalesName" },
              { headerName: "SubSubBL", field: "SubSubBL" },
              { headerName: "NBI (K€)", field: "Ytd" },
            ],
          });
          this.setDataStatus("ready");
        }
      } else {
        this.setState({
          data: result,
        });
        this.setDataStatus("no-data");
      }
    }
  }

  /**
   * To hide and show panels
   */
  private panelVisibility(
    showSales: boolean,
    showLe: boolean,
    showDeal: boolean
  ) {
    this.setState({
      showDealPanel: showDeal,
      showLePanel: showLe,
      showSalesPanel: showSales,
    });
  }
}

export const NbiYtdTops = withWidgetConfiguration(NbiYtdTopsClass);
